import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ListVideo, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { api, errorMessage, useApi } from "@/lib/api";
import { fmtDate, fmtDuration } from "@/lib/format";
import type { Playlist } from "@/lib/types";
import { EmptyState, Loading, PageHeader } from "@/components/app/PageHeader";

export default function PlaylistsPage() {
  const navigate = useNavigate();
  const { data, loading } = useApi<Playlist[]>("/playlists");
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");

  const create = async () => {
    try {
      const { data: p } = await api.post<Playlist>("/playlists", { name, items: [] });
      navigate(`/listes/${p.id}`);
    } catch (e) { toast.error(errorMessage(e)); }
  };
  const btn = <Button data-testid="btn-create-playlist" onClick={() => { setName(""); setOpen(true); }} className="bg-blue-600 text-white hover:bg-blue-500"><Plus className="mr-1 h-4 w-4" />Nouvelle liste</Button>;

  return (
    <div>
      <PageHeader eyebrow="Contenu" title="Listes de lecture" description="Ordonnez vos médias et définissez la durée d'affichage de chacun." actions={btn} />
      {loading && !data ? <Loading /> : !data?.length ? (
        <EmptyState testId="playlists-empty" icon={<ListVideo className="h-6 w-6" />} title="Aucune liste" text="Créez une liste pour regrouper vos médias." action={btn} />
      ) : (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {data.map((p, i) => (
            <Link key={p.id} to={`/listes/${p.id}`} data-testid={`playlist-card-${p.id}`} style={{ animationDelay: `${i * 50}ms` }} className="rise panel group p-6 transition-colors hover:border-slate-700">
              <ListVideo className="h-5 w-5 text-cyan-400" />
              <h3 className="mt-4 font-display text-lg font-medium text-slate-100 group-hover:text-blue-300">{p.name}</h3>
              <div className="mt-4 flex gap-6 text-xs text-slate-400">
                <span><span className="block font-mono text-lg text-slate-200">{p.items.length}</span>éléments</span>
                <span><span className="block font-mono text-lg text-slate-200">{fmtDuration(p.total_seconds)}</span>boucle</span>
              </div>
              <p className="mt-4 text-xs text-slate-500">Modifiée {fmtDate(p.updated_at)}</p>
            </Link>
          ))}
        </div>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="border-slate-800 bg-slate-900">
          <DialogHeader><DialogTitle>Nouvelle liste de lecture</DialogTitle></DialogHeader>
          <Input data-testid="playlist-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Menu du midi" className="border-slate-700 bg-slate-950/60" />
          <Button data-testid="btn-submit-playlist" disabled={!name.trim()} onClick={create} className="bg-blue-600 text-white hover:bg-blue-500">Créer et éditer</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
